/**
 * Emoji tokenization for the editorial typesetting layer.
 *
 * Pure: no React, no DOM. Every display adapter consumes this one source-offset
 * token model so a glyph is recognised identically wherever it renders.
 */

import emojiRegex from "emoji-regex";

import {
    POLLY_EDITORIAL_V1,
    type EditorialEmojiDisposition,
    type EditorialPolicyManifest,
    type EditorialSemantic,
    type EditorialVocabularyEntry,
} from "./editorial-policy";

/**
 * Constructed once from the factory and never exported, not even for tests.
 * The expression carries the global flag, so its `lastIndex` is shared mutable
 * state: a caller that kept a reference could leave it mid-string and silently
 * skip the next scan's prefix.
 */
const MATCHER = emojiRegex();

export interface EditorialTextRun {
    readonly kind: "text";
    readonly source: string;
    readonly start: number;
    readonly end: number;
}

export interface EditorialEmojiToken {
    readonly kind: "emoji";
    readonly disposition: EditorialEmojiDisposition;
    readonly semantic?: EditorialSemantic;
    readonly source: string;
    readonly start: number;
    readonly end: number;
}

export type EditorialTextSegment = EditorialTextRun | EditorialEmojiToken;

export type EditorialScanResult =
    | { readonly changed: false; readonly text: string }
    | {
          readonly changed: true;
          readonly text: string;
          readonly segments: readonly EditorialTextSegment[];
      };

export interface EditorialScanOptions {
    readonly policy?: EditorialPolicyManifest;
}

/**
 * Whether the test-only instrumentation below is live.
 *
 * `import.meta.env` is supplied by Vite and by nothing else, so a pure-node
 * consumer — the verification harness, or an esbuild probe — has no such
 * object, so every dereference sits behind the `typeof` guard. Under Vite the
 * whole expression still folds to a literal, which is what keeps the branches
 * below out of the bundle.
 */
const DEV_INSTRUMENTATION =
    typeof import.meta.env !== "undefined" && import.meta.env.DEV === true;

/**
 * Test-only. Proving the single-pass contract needs an observable, and spying
 * on the module-private matcher would be more brittle than counting here.
 *
 * Every read and the write in `scanEditorialText` sit behind
 * `DEV_INSTRUMENTATION`, which a production build folds to `false` — so the
 * branch is dead code and these accessors lose their last reference, leaving
 * the shipped scanner with no test-only weight and no mutable global on the
 * render path.
 */
let scanPasses = 0;

export const readScanStats = (): { readonly passes: number } => ({
    passes: DEV_INSTRUMENTATION ? scanPasses : 0,
});

export const resetScanStats = (): void => {
    if (DEV_INSTRUMENTATION) scanPasses = 0;
};

/** Strip U+FE0F for map lookup only; the token keeps its original source. */
export const canonicalEmojiKey = (sequence: string): string =>
    sequence.replace(/\uFE0F/g, "");

interface PolicyLookup {
    readonly byCanonicalKey: ReadonlyMap<string, EditorialVocabularyEntry>;
    readonly preservedSymbols: ReadonlySet<string>;
}

/**
 * Built once per policy object. Keyed weakly so a caller-supplied policy is
 * collectable, and holding only manifest data — never conversation text.
 */
const LOOKUPS = new WeakMap<EditorialPolicyManifest, PolicyLookup>();

const lookupFor = (policy: EditorialPolicyManifest): PolicyLookup => {
    const cached = LOOKUPS.get(policy);
    if (cached) return cached;

    const built: PolicyLookup = {
        byCanonicalKey: new Map(
            policy.vocabulary.map((entry) => [entry.canonicalKey, entry])
        ),
        preservedSymbols: new Set(policy.preservedSymbols),
    };
    LOOKUPS.set(policy, built);
    return built;
};

interface ResolvedDisposition {
    readonly disposition: EditorialEmojiDisposition;
    readonly semantic?: EditorialSemantic;
}

/**
 * Pure map lookups only. This runs inside the scan loop while the matcher holds
 * a mid-string `lastIndex`, so it must never re-enter `scanEditorialText` — a
 * nested scan's `finally` would reset that index and restart the outer loop.
 *
 * Order is load-bearing: a pinned preserved symbol can never be reclassified,
 * an exact canonical-key hit decides the rest, and everything else takes the
 * manifest's fail-safe. There is no generic-strip branch.
 */
const resolveDisposition = (
    source: string,
    policy: EditorialPolicyManifest,
    lookup: PolicyLookup
): ResolvedDisposition => {
    // Matched raw, not canonicalised: the manifest pins the BARE symbol, and
    // an explicitly emoji-presented form (© + U+FE0F) is a different token the
    // vocabulary is free to classify.
    if (lookup.preservedSymbols.has(source)) return { disposition: "PRESERVE" };

    const entry = lookup.byCanonicalKey.get(canonicalEmojiKey(source));
    if (!entry) return { disposition: policy.fallbackDisposition };

    return entry.disposition === "SEMANTIC"
        ? { disposition: "SEMANTIC", semantic: entry.semantic }
        : { disposition: entry.disposition };
};

export const scanEditorialText = (
    text: string,
    options: EditorialScanOptions = {}
): EditorialScanResult => {
    const policy = options.policy ?? POLLY_EDITORIAL_V1;

    MATCHER.lastIndex = 0;
    if (DEV_INSTRUMENTATION) scanPasses += 1;

    try {
        let match = MATCHER.exec(text);
        // The fast path returns before anything is allocated. Emoji-free
        // prose is the common case and the scanner runs per eligible text run,
        // so an array built here and discarded would be built on every run.
        if (match === null) return { changed: false, text };

        const segments: EditorialTextSegment[] = [];
        // Resolved on first match, not before it: a policy that cannot be read
        // must fail with the matcher mid-string, so the `finally` reset below
        // is the thing that keeps the next scan correct.
        let lookup: PolicyLookup | null = null;
        let cursor = 0;

        while (match !== null) {
            const start = match.index;
            const end = start + match[0].length;

            // A zero-length match would never advance `lastIndex` and the loop
            // would not terminate. The matcher should not produce one; this
            // costs nothing and removes the possibility.
            if (end === start) {
                MATCHER.lastIndex = start + 1;
                match = MATCHER.exec(text);
                continue;
            }

            if (start > cursor) {
                segments.push({
                    kind: "text",
                    source: text.slice(cursor, start),
                    start: cursor,
                    end: start,
                });
            }

            lookup ??= lookupFor(policy);
            segments.push({
                kind: "emoji",
                ...resolveDisposition(match[0], policy, lookup),
                source: match[0],
                start,
                end,
            });

            cursor = end;
            match = MATCHER.exec(text);
        }

        if (segments.length === 0) return { changed: false, text };

        if (cursor < text.length) {
            segments.push({
                kind: "text",
                source: text.slice(cursor),
                start: cursor,
                end: text.length,
            });
        }

        return { changed: true, text, segments };
    } finally {
        MATCHER.lastIndex = 0;
    }
};

// Horizontal only: a removal must never swallow an authored line boundary.
const TRAILING_HORIZONTAL_WS = /[^\S\n\r]+$/;
const LEADING_HORIZONTAL_WS = /^[^\S\n\r]+/;
const HORIZONTAL_WS_CHAR = /[^\S\n\r]/;
export const WORD_LIKE = /[\p{L}\p{N}_]/u;

/** First offset at or left of `from` that is not horizontal whitespace. */
export const expandLeft = (text: string, from: number, floor = 0): number => {
    let at = from;
    while (at > floor && HORIZONTAL_WS_CHAR.test(text.charAt(at - 1))) at -= 1;
    return at;
};

/** First offset at or right of `from` that is not horizontal whitespace. */
export const expandRight = (text: string, from: number): number => {
    let at = from;
    while (at < text.length && HORIZONTAL_WS_CHAR.test(text.charAt(at)))
        at += 1;
    return at;
};
const OPENING_PUNCTUATION = new Set([
    "(",
    "[",
    "{",
    "<",
    '"',
    "'",
    "\u201C",
    "\u2018",
]);

// Always debris: an unpaired surrogate is not valid text in any script, so it
// is trimmed regardless of what sits beside it.
const LONE_SURROGATE_TAIL = /[\uD800-\uDBFF](?![\uDC00-\uDFFF])$/;

/**
 * Emoji continuation characters, which are orphans only when emoji-ADJACENT.
 * ZWJ especially carries orthographic meaning in Devanagari, Arabic and
 * Malayalam, so a joiner following ordinary letters is authored content and
 * must survive — the grammar "does not blindly strip ZWJ from unrelated
 * scripts".
 */
const CONTINUATION_TAIL =
    /(?:\u200D|\uFE0F|\u20E3|[\u{1F1E6}-\u{1F1FF}]|[\u{1F3FB}-\u{1F3FF}]|[\u{E0020}-\u{E007F}])+$/u;

// Invisible formatting characters. The seam rule is stated in terms of what a
// reader sees, so the decision must look PAST these to the first real character:
// a joiner sitting between a removal and the next word renders as nothing, and
// testing the raw code unit would suppress the space and fuse two words.
// Alternation, not one class: `no-misleading-character-class` rightly objects
// to a class mixing a variation selector and a joiner, and here they are
// wanted individually.
const ZERO_WIDTH = /\u00AD|\u061C|[\u200B-\u200F]|\u2060|\uFE0F|\uFEFF/;

// Both scans step by CODE POINT, not code unit. Indexing a string yields half
// of an astral character, and `WORD_LIKE` (a `\p{L}` class) does not match a
// lone surrogate — so a code-unit scan reported "not word-like" for every
// astral letter and the seam space was dropped, fusing the words either side.
export const firstVisible = (value: string, start = 0): string | undefined => {
    for (let i = start; i < value.length; ) {
        const codePoint = value.codePointAt(i);
        if (codePoint === undefined) return undefined;

        const char = String.fromCodePoint(codePoint);
        if (!ZERO_WIDTH.test(char)) return char;
        i += char.length;
    }
    return undefined;
};

const HIGH_SURROGATE_START = 0xd800;
const LOW_SURROGATE_START = 0xdc00;
const SURROGATE_END = 0xdfff;

export const lastVisible = (
    value: string,
    initialEnd = value.length
): string | undefined => {
    for (let end = initialEnd; end > 0; ) {
        // Stepping back needs the pair test explicitly: `codePointAt` reads
        // FORWARD, so it cannot tell a standalone character from the trailing
        // half of one. Walking the whole string as code points instead would
        // allocate across the entire accumulated output on every removal.
        const trailing = value.charCodeAt(end - 1);
        const paired =
            trailing >= LOW_SURROGATE_START &&
            trailing <= SURROGATE_END &&
            end >= 2 &&
            value.charCodeAt(end - 2) >= HIGH_SURROGATE_START &&
            value.charCodeAt(end - 2) < LOW_SURROGATE_START;
        const start = paired ? end - 2 : end - 1;

        const char = value.slice(start, end);
        if (!ZERO_WIDTH.test(char)) return char;
        end = start;
    }
    return undefined;
};

/**
 * Seam policy, applied only at a removal site — never as a global collapse.
 * Whitespace on either side has already been stripped by the caller, so this
 * decides solely whether the two sides need one space between them.
 */
const seamBetween = (before: string, after: string): string => {
    if (before === "" || after === "") return "";

    const afterChar = firstVisible(after);
    const beforeChar = lastVisible(before);
    return seamBetweenVisible(beforeChar, afterChar);
};

const seamBetweenVisible = (
    beforeChar: string | undefined,
    afterChar: string | undefined
): string => {
    if (afterChar === undefined || beforeChar === undefined) return "";
    if (afterChar === "\n" || afterChar === "\r") return "";
    if (OPENING_PUNCTUATION.has(beforeChar)) return "";
    if (OPENING_PUNCTUATION.has(afterChar) && WORD_LIKE.test(beforeChar)) {
        return " ";
    }
    if (!WORD_LIKE.test(afterChar)) return "";

    return " ";
};

export interface EditorialRemovalSeam {
    /** Run-text offset where the removal begins, whitespace included. */
    readonly start: number;
    readonly end: number;
    /** What replaces the removed span — "" or a single space. */
    readonly replacement: string;
}

/**
 * The seam decision for ONE removal, expressed as a span and its replacement so
 * a tree-level splice can reproduce exactly what `toDisplayText` would produce
 * on the flat string.
 *
 * Exported so the plugin does not restate the seam rule. A second copy of
 * "strip the whitespace either side, then decide whether one space goes back"
 * is the drift bug the seam matrix exists to prevent — the two would agree on
 * the corpus and diverge on the first case nobody wrote down.
 */
export const resolveRemovalSeam = (
    text: string,
    start: number,
    end: number
): EditorialRemovalSeam => {
    const seamStart = expandLeft(text, start);
    const seamEnd = expandRight(text, end);

    return {
        start: seamStart,
        end: seamEnd,
        replacement: seamBetweenVisible(
            lastVisible(text, seamStart),
            firstVisible(text, seamEnd)
        ),
    };
};

export const toDisplayText = (result: EditorialScanResult): string => {
    // No emoji matched anywhere, so nothing here can be an emoji fragment
    // except an unpaired surrogate. A joiner in this text belongs to a script,
    // not to a sequence.
    if (!result.changed) return result.text.replace(LONE_SURROGATE_TAIL, "");

    let out = "";
    let removalPending = false;

    for (const segment of result.segments) {
        if (segment.kind === "emoji" && segment.disposition === "DECORATIVE") {
            out = out.replace(TRAILING_HORIZONTAL_WS, "");
            removalPending = true;
            continue;
        }

        let piece = segment.source;
        if (removalPending) {
            piece = piece.replace(LEADING_HORIZONTAL_WS, "");
            out += seamBetween(out, piece);
            removalPending = false;
        }
        out += piece;
    }

    // The gate is the last segment that actually REACHED the output. A removed
    // decorative emoji contributes nothing, so gating on the last segment
    // outright left an orphan that preceded one at the display tail.
    let lastRenderedIndex = -1;
    for (let i = result.segments.length - 1; i >= 0; i--) {
        const segment = result.segments[i];
        if (segment.kind === "emoji" && segment.disposition === "DECORATIVE") {
            continue;
        }
        lastRenderedIndex = i;
        break;
    }

    const lastRendered = result.segments[lastRenderedIndex];
    if (lastRendered?.kind !== "text") return out;

    out = out.replace(LONE_SURROGATE_TAIL, "");

    const tail = CONTINUATION_TAIL.exec(lastRendered.source)?.[0] ?? "";
    if (tail === "") return out;

    // Emoji-adjacent on one side or the other: the run either abuts the
    // preceding token (nothing else in this text run) or was joined to a
    // decorative glyph that has just been removed. A run sitting after ordinary
    // letters with no emoji beside it is authored script content.
    const abutsPreviousToken = lastRendered.source.length === tail.length;
    const abutsRemovedGlyph = lastRenderedIndex < result.segments.length - 1;
    if (!abutsPreviousToken && !abutsRemovedGlyph) return out;

    return out.replace(CONTINUATION_TAIL, "");
};

/**
 * Whether typesetting this source leaves the reader anything at all.
 *
 * Callers use it to decide whether to render chrome around the result: an
 * inferred table title that was nothing but a decorative mark has no display
 * text left, and a bold empty span holding open a slot that says nothing is
 * worse than no slot.
 *
 * A mapped glyph counts as visible — the text becomes a mark, which still
 * carries the meaning. Exported because the React-children adapter asks the
 * same question of a link label; two independently-maintained answers to
 * "does anything survive?" is the drift this layer keeps designing against.
 */
export const hasVisibleDisplay = (text: string): boolean => {
    const scan = scanEditorialText(text);
    if (!scan.changed) return text.trim() !== "";

    // A mapped emoji needs no special case here: `toDisplayText` keeps its
    // source character, so a source whose whole display is a MARK still
    // measures as non-empty and its chrome is kept. If `toDisplayText` ever
    // drops semantic tokens, the "counts a mapped glyph as visible" case
    // fails, which is where that change should surface.
    return toDisplayText(scan).trim() !== "";
};
